export const uiUxDesignData = {
  hero: {
    badge: "UI/UX & Product Design",
    headline: "Interfaces Designed for Usability and Conversion",
    subtext:
      "Modern UI/UX design, design systems and product experiences that make your web, mobile and SaaS products intuitive, accessible and conversion-focused.",
    primaryCTA: "Start Your Project",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  capabilities: [
    { title: "UI/UX Design", desc: "User-centred interface design focused on clarity and ease of use.", icon: "ri-palette-line" },
    { title: "Web Design", desc: "Responsive, high-quality web experiences that feel premium.", icon: "ri-window-line" },
    { title: "Mobile App UI/UX", desc: "Mobile-first flows designed for touch, speed and engagement.", icon: "ri-smartphone-line" },
    { title: "Dashboard Design", desc: "Data-dense dashboards with clear hierarchy and readability.", icon: "ri-bar-chart-box-line" },
    { title: "SaaS Product Design", desc: "End-to-end product design for multi-tenant SaaS platforms.", icon: "ri-cloud-line" },
    { title: "Design Systems", desc: "Reusable component libraries that keep products consistent.", icon: "ri-layout-masonry-line" },
    { title: "Wireframing & Prototyping", desc: "Low to high-fidelity prototypes for fast validation.", icon: "ri-draft-line" },
    { title: "Usability & Accessibility", desc: "Interfaces that are accessible and easy for every user.", icon: "ri-eye-line" },
  ],
  benefits: [
    { title: "Higher Conversion", desc: "Clear layouts and persuasive flows turn visitors into customers.", icon: "ri-line-chart-line" },
    { title: "Lower Development Cost", desc: "Validated design reduces rework and costly changes later.", icon: "ri-money-dollar-circle-line" },
    { title: "Consistent Branding", desc: "A coherent visual language across every touchpoint.", icon: "ri-shield-star-line" },
    { title: "Faster Time to Market", desc: "Design systems and prototypes speed up the build.", icon: "ri-rocket-line" },
  ],
  process: [
    { number: "01", title: "Research", desc: "Understand users, goals and the competitive landscape." },
    { number: "02", title: "Wireframe", desc: "Map out structure, hierarchy and user flows." },
    { number: "03", "title": "Design", desc: "Craft a premium, on-brand visual interface." },
    { number: "04", title: "Prototype", desc: "Interactive clickable prototypes for feedback." },
    { number: "05", title: "Handoff", desc: "Developer-ready specs and design system." },
    { number: "06", title: "Support", desc: "Design QA and iteration during development." },
  ],
  faq: [
    { q: "Do you design only for Laravel applications?", a: "No. While we specialise in Laravel backends, our UI/UX team designs web, mobile and SaaS products across technologies. The design work integrates seamlessly with any development stack." },
    { q: "Can you redesign an existing application?", a: "Yes. We audit the current experience, identify usability and conversion gaps, and deliver a modern redesign while keeping what already works." },
    { q: "Do you include a design system?", a: "For product and SaaS projects we recommend a design system, so your team stays consistent as the product grows. It is included in larger engagements." },
    { q: "How do you collaborate with developers?", a: "We hand off clean, developer-ready designs with tokens, spacing rules and component specs, and stay involved through implementation for design QA." },
  ],
  cta: {
    heading: "Need a Design That Converts?",
    text: "Let's turn your product into a clear, premium experience your users love.",
    primaryCTA: "Start Your Project",
    secondaryCTA: "Talk to a Laravel Expert",
  },
};

export const maintenanceData = {
  hero: {
    badge: "Maintenance & Support",
    headline: "Keep Your Software Fast, Secure and Reliable",
    subtext:
      "Long-term technical support, monitoring, bug fixing and continuous improvement so your application stays performant, secure and up to date.",
    primaryCTA: "Get Technical Support",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  capabilities: [
    { title: "Application Maintenance", desc: "Ongoing care to keep your product stable and healthy.", icon: "ri-tools-line" },
    { title: "Bug Fixing & Hotfixes", desc: "Rapid resolution of issues and critical patches.", icon: "ri-bug-line" },
    { title: "Performance Optimization", desc: "Speed up queries, APIs and page load times.", icon: "ri-speed-line" },
    { title: "Security Updates", desc: "Proactive patching and security hardening.", icon: "ri-shield-check-line" },
    { title: "Server & Infrastructure Care", desc: "Monitoring, backups and infrastructure reliability.", icon: "ri-server-line" },
    { title: "Feature Enhancements", desc: "Small to medium improvements on a regular cadence.", icon: "ri-add-circle-line" },
    { title: "24/7 Emergency Support", desc: "Rapid response when issues affect your business.", icon: "ri-alarm-line" },
    { title: "Long-Term Partnership", desc: "A reliable technical partner for the life of your product.", icon: "ri-heart-3-line" },
  ],
  benefits: [
    { title: "Reduced Downtime", desc: "Proactive monitoring catches issues before users notice.", icon: "ri-time-line" },
    { title: "Lower Long-Term Cost", desc: "Preventive maintenance avoids expensive emergency fixes.", icon: "ri-money-dollar-circle-line" },
    { title: "Peace of Mind", desc: "A dedicated team watching over your product.", icon: "ri-shield-check-line" },
    { title: "Continuous Improvement", desc: "Your product keeps evolving with the market.", icon: "ri-arrow-up-circle-line" },
  ],
  process: [
    { number: "01", title: "Audit", desc: "Assess codebase, infrastructure and risks." },
    { number: "02", title: "Plan", desc: "Define scope, SLAs and maintenance cadence." },
    { number: "03", title: "Monitor", desc: "Active monitoring of uptime, errors and performance." },
    { number: "04", title: "Maintain", desc: "Updates, security patches and optimizations." },
    { number: "05", title: "Improve", desc: "Feature enhancements and refinements." },
    { number: "06", title: "Report", desc: "Regular transparent status reports." },
  ],
  faq: [
    { q: "Which technologies do you support?", a: "We primarily support Laravel, PHP and JavaScript applications, along with their cloud infrastructure on AWS, DigitalOcean and similar providers." },
    { q: "Do you provide 24/7 support?", a: "We offer 24/7 emergency support on our priority plans, with defined response time SLAs. Standard plans provide support during business hours." },
    { q: "Can you take over an application built by another team?", a: "Yes. We start with a full codebase and infrastructure audit, then put a maintenance plan in place. We make the handover smooth and low-risk." },
    { q: "How is pricing structured?", a: "We offer flexible monthly retainer packages or per-project support. Pricing depends on the size and criticality of your application." },
  ],
  cta: {
    heading: "Need Reliable Long-Term Support?",
    text: "Let's keep your application secure, fast and always available.",
    primaryCTA: "Get Technical Support",
    secondaryCTA: "Start Your Project",
  },
};